import React, { FC, useEffect, useState } from 'react'
import { NumberParam, StringParam, useQueryParam } from 'use-query-params'
import { useTranslation } from 'react-i18next'
import { Button, SearchComponent, StartPage } from '../../../components'
import { useNavigate } from 'react-router-dom'
import { staffObjectStorageService } from '../../../api'
import { useUi } from '../../../context/ui.context'
import { usePageTitle } from '../../../context/navigationContext'
import { timeConverterMMDDYYHHMM } from '../../../utils/dates'
import { ClickableName, StatusBadge } from './styles'
import { getObjectStorageError } from './utils'

const ObjectUsers: FC<{ userListPath: string }> = ({ userListPath }) => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  usePageTitle(t('OBJECT_STORAGE_USERS'))
  const [page] = useQueryParam('page', NumberParam)
  const [pageSize] = useQueryParam('page_size', NumberParam)
  const [ordering] = useQueryParam('ordering', StringParam)
  const [search] = useQueryParam('search', StringParam)
  const [users, setUsers] = useState<any[]>([])
  const [pagination, setPagination] = useState<any>()
  const [pageLoading, setPageLoading] = useState(true)
  const [loading, setLoading] = useState(false)

  const loadUsers = () => {
    setLoading(true)
    staffObjectStorageService.getUsers({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering || '-created_at',
      search: search || '',
    })
      .then(({ data }) => {
        setPagination(data)
        setUsers(data.objects.map((objectUser) => ({
          ...objectUser,
          ceph_uid: (
            <ClickableName onClick={() => navigate(`${userListPath}/${objectUser.id}`)}>
              {objectUser.ceph_uid || objectUser.id}
            </ClickableName>
          ),
          client_name: objectUser.client.display || objectUser.client.name,
          service_name: objectUser.service?.display_name || 'N/A',
          disabled: (
            <StatusBadge $active={!objectUser.deleted && !objectUser.disabled}>
              {objectUser.deleted
                ? t('DELETED')
                : objectUser.disabled ? t('DISABLED') : t('ACTIVE')}
            </StatusBadge>
          ),
          disabled_reason_display: objectUser.disabled_reason
            ? t(`OBJECT_STORAGE_REASON_${objectUser.disabled_reason.toUpperCase()}`)
            : 'N/A',
          created_at: timeConverterMMDDYYHHMM(objectUser.created_at),
        })))
      })
      .catch((error) => viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      }))
      .finally(() => {
        setPageLoading(false)
        setLoading(false)
      })
  }

  useEffect(() => {
    loadUsers()
  }, [page, pageSize, ordering, search])

  const columns = [
    { title: t('CEPH_UID'), key: 'ceph_uid', sorted: true },
    { title: t('CLIENT'), key: 'client_name' },
    { title: t('SERVICE'), key: 'service_name' },
    { title: t('STATUS'), key: 'disabled', sorted: true },
    { title: t('DISABLED_REASON'), key: 'disabled_reason_display' },
    { title: t('BUCKETS'), key: 'bucket_count' },
    { title: t('CREATED_AT'), key: 'created_at', sorted: true },
  ]

  return (
    <StartPage
      title={`${t('OBJECT_STORAGE_USERS')}`}
      noResourceText={`${t('NO_OBJECT_STORAGE_USERS')}`}
      haveResource={users.length > 0}
      pageLoading={pageLoading}
      withoutCreateResourceButton
      rightCustomCreateButton={(
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: '200px' }}><SearchComponent /></div>
          <Button
            variant="stroke"
            size="display"
            title={t('REFRESH')}
            loading={loading}
            onClick={loadUsers}
          />
        </div>
      )}
      tableData={{
        columns,
        data: users,
        paginationData: pagination,
        updateData: loadUsers,
        hideCheckbox: true,
        loading,
      }}
    />
  )
}

export default ObjectUsers
